import BaseNotificationHandler from './baseHandler.js';

/**
 * Reaction Notification Handler
 * Handles post reaction notifications (like, love, etc.)
 */
class ReactionHandler extends BaseNotificationHandler {
  getIcon(notification, parsedData) {
    // Custom emoji image paths
    const HappyEmoji = "/icons/Reactions/Sulhafa_Emoji_Happy.png";
    const LoveEmoji = "/icons/Reactions/Sulhafa_Emoji_Love.png";
    const LaughEmoji = "/icons/Reactions/Sulhafa_Emoji_Laugh.png";
    const ShockedEmoji = "/icons/Reactions/Sulhafa_Emoji_Shocked.png";
    const SadEmoji = "/icons/Reactions/Sulhafa_Emoji_Sad.png";
    const AngryEmoji = "/icons/Reactions/Sulhafa_Emoji_Angry.png";
    const reactionType = parsedData.reaction_type || 'like';
    const reactionTypeToEmoji = {
      'like': HappyEmoji,
      'love': LoveEmoji,
      'funny': LaughEmoji,
      'haha': LaughEmoji,
      'laugh': LaughEmoji,
      'wow': ShockedEmoji,
      'sad': SadEmoji,
      'angry': AngryEmoji
    };
    return reactionTypeToEmoji[reactionType] || HappyEmoji;
  }

  getMainText(notification, parsedData) {
    const senderName = this.getSenderInfo(notification, parsedData) || 'Someone';
    const reactionType = parsedData.reaction_type || 'like';
    const reactionLabels = {
      'like': 'was confused about',
      'love': 'loved',
      'funny': 'laughed at',
      'haha': 'laughed at',
      'laugh': 'laughed at',
      'wow': 'was surprised by',
      'sad': 'was sad about',
      'angry': 'was angry about'
    };
    const action = reactionLabels[reactionType] || 'reacted to';

    // Check if this is a reaction to a comment
    const contentType = parsedData.content_type || parsedData.data?.content_type;
    const isCommentReaction = contentType && (
      contentType === 'App\\Models\\PostComment' ||
      contentType === 'App\\\\Models\\\\PostComment' ||
      contentType.includes('PostComment')
    );

    let target = 'post';
    if (isCommentReaction) target = 'comment';
    if (contentType && contentType.includes('Story')) target = 'story';

    return `${senderName} ${action} your ${target}`;
  }

  getDetails(notification, parsedData) {
    // Custom emoji image paths
    const HappyEmoji = "/icons/Reactions/Sulhafa_Emoji_Happy.png";
    const LoveEmoji = "/icons/Reactions/Sulhafa_Emoji_Love.png";
    const LaughEmoji = "/icons/Reactions/Sulhafa_Emoji_Laugh.png";
    const ShockedEmoji = "/icons/Reactions/Sulhafa_Emoji_Shocked.png";
    const SadEmoji = "/icons/Reactions/Sulhafa_Emoji_Sad.png";
    const AngryEmoji = "/icons/Reactions/Sulhafa_Emoji_Angry.png";
    const details = [];
    const reactionType = parsedData.reaction_type || 'like';
    const reactionEmojis = {
      'like': HappyEmoji,
      'love': LoveEmoji,
      'funny': LaughEmoji,
      'haha': LaughEmoji,
      'laugh': LaughEmoji,
      'wow': ShockedEmoji,
      'sad': SadEmoji,
      'angry': AngryEmoji
    };
    const emoji = reactionEmojis[reactionType] || HappyEmoji;
    // Store emoji path as object so we can render it as image (matching NotificationDropdown format)
    const contentType = parsedData.content_type || parsedData.data?.content_type;
    const isStoryReact = contentType && contentType.includes('Story');

    if (!isStoryReact) {
      details.push({ type: 'reaction', emojiPath: emoji });
    }

    return details;
  }

  getThumbnail(notification, parsedData) {
    // For reactions, try to get post image
    if (parsedData.post_image) return parsedData.post_image;
    if (parsedData.image) return parsedData.image;
    if (parsedData.media?.[0]?.url) return parsedData.media[0].url;
    if (parsedData.data?.post_image) return parsedData.data.post_image;
    if (parsedData.data?.image) return parsedData.data.image;
    return super.getThumbnail(notification, parsedData);
  }

  getActionUrl(notification, parsedData) {
    // Check parsedData.action_url first (outer level)
    if (parsedData.action_url) {
      return parsedData.action_url;
    }

    // Check nested data.action_url (from double-encoded JSON)
    if (parsedData.data?.action_url) {
      return parsedData.data.action_url;
    }

    // Check notification.action_url as fallback
    if (notification.action_url) {
      return notification.action_url;
    }

    // Build post URL from content_id if available
    const contentId = parsedData.content_id || parsedData.data?.content_id;
    const contentType = parsedData.content_type || parsedData.data?.content_type;

    if (contentId && contentType) {
      // Handle Post reactions
      if (
        contentType === 'App\\Models\\Post' ||
        contentType === 'App\\\\Models\\\\Post' ||
        (contentType.includes('Post') && !contentType.includes('Comment'))
      ) {
        return `/post/${contentId}`;
      }

      // Handle PostComment reactions - navigate to the post containing the comment
      // The action_url should already contain the correct post URL, but if not,
      // we'll use the content_id (comment ID) which the frontend may handle
      if (
        contentType === 'App\\Models\\PostComment' ||
        contentType === 'App\\\\Models\\\\PostComment' ||
        contentType.includes('PostComment')
      ) {
        // Try to get post_id from parsedData if available
        const postId = parsedData.post_id || parsedData.data?.post_id;
        if (postId) {
          return `/post/${postId}`;
        }
        // Fallback to using content_id (comment ID) - frontend may handle scrolling to comment
        return `/post/${contentId}`;
      }
    }

    return null;
  }

  /**
   * Get post ID from notification data
   * @param {Object} notification - The notification object
   * @param {Object} parsedData - Parsed notification data
   * @returns {number|null} Post ID or null
   */
  getPostId(notification, parsedData) {
    const contentType = parsedData.content_type || parsedData.data?.content_type;
    const contentId = parsedData.content_id || parsedData.data?.content_id;

    // For Story reactions, do not return a post ID
    if (contentType && contentType.includes('Story')) {
      return null;
    }

    // For Post reactions, content_id is the post_id
    if (contentType && (
      contentType === 'App\\Models\\Post' ||
      contentType === 'App\\\\Models\\\\Post' ||
      (contentType.includes('Post') && !contentType.includes('Comment'))
    )) {
      return contentId;
    }

    // For PostComment reactions, try to get post_id
    if (contentType && (
      contentType === 'App\\Models\\PostComment' ||
      contentType === 'App\\\\Models\\\\PostComment' ||
      contentType.includes('PostComment')
    )) {
      // Try to get post_id from parsedData
      const postId = parsedData.post_id || parsedData.data?.post_id;
      if (postId) {
        return postId;
      }
      // If not available, we might need to fetch it, but for now return contentId
      // The NotificationDropdown can handle fetching if needed
      return contentId;
    }

    // Fallback: try to get post_id directly
    return parsedData.post_id || parsedData.data?.post_id || contentId;
  }

  /**
   * Get comment ID from notification data (if reaction is to a comment)
   * @param {Object} notification - The notification object
   * @param {Object} parsedData - Parsed notification data
   * @returns {number|null} Comment ID or null
   */
  getCommentId(notification, parsedData) {
    const contentType = parsedData.content_type || parsedData.data?.content_type;
    if (contentType && (
      contentType === 'App\\Models\\PostComment' ||
      contentType === 'App\\\\Models\\\\PostComment' ||
      contentType.includes('PostComment')
    )) {
      return parsedData.content_id || parsedData.data?.content_id;
    }
    return null;
  }

  handleClick(notification, parsedData, navigate, onClose, openPostModal = null) {
    const contentType = parsedData.content_type || parsedData.data?.content_type;

    // For Story reactions, we don't navigate anywhere, just close the menu
    if (contentType && contentType.includes('Story')) {
      if (onClose) onClose();
      return;
    }

    // If openPostModal is provided, use it to open the PostDetailModal
    if (openPostModal) {
      const postId = this.getPostId(notification, parsedData);
      const commentId = this.getCommentId(notification, parsedData);
      if (postId) {
        openPostModal(postId, commentId);
        if (onClose) onClose();
        return;
      }
    }

    // Fallback to navigation if openPostModal is not available or postId is not found
    const actionUrl = this.getActionUrl(notification, parsedData);
    if (actionUrl) {
      if (actionUrl.startsWith('http://') || actionUrl.startsWith('https://')) {
        window.location.href = actionUrl;
      } else {
        navigate(actionUrl);
      }
      if (onClose) onClose();
    }
  }
}

export default ReactionHandler;

